import { Box, Center, Grid, Icon, IconButton, SimpleGrid, Text, useDisclosure } from '@chakra-ui/react'

import { useState } from 'react'
import { getSkillLevelDisplay } from '../../functions/Capitalize'
import useMobile from '../../hooks/useMobile'
import { isSameDay } from '../../pages/user/NotificationEvents'
import { ArrowLeft } from '../icons/icons'
import ExtraOptionsModal from '../modals/ExtraOptionsModal'
import { getEventsOfTheDay } from './CalendarLarge'

import EventDetailCard from '../cards/EventDetailCard'
import PrivateClassJoinedCard from '../cards/PrivateClassJoinedCard'

const DAYS_OF_WEEK = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
// const SKILL_LEVELS: any = ['Beginner', 'Intermediate', 'Intermediate High', 'Advanced', 'Advanced 4.5+', 'Mixed-Level']

const CalendarDisplay = ({
  events,
  onShareOpen,
  setCurrentEventId,
}: {
  events: any
  onShareOpen: () => void
  setCurrentEventId: (v: string) => void
}) => {
  const currentDate = new Date()
  const currentMonth = currentDate.getMonth()

  const isMobile = useMobile(600)
  const isTablet = useMobile(1130)
  const isSmallTablet = useMobile(980)
  const isBigMobile = useMobile(850)

  const [dateToShow, setDateToShow] = useState<Date | null>(null)

  const { isOpen: isExtraOptionsOpen, onOpen: onExtraOptionsOpen, onClose: onExtraOptionsClose } = useDisclosure()

  const getMonthName = (month: number) => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    return monthNames[month]
  }

  const getDaysInMonth = (month: number) => {
    const year = currentDate.getFullYear()
    return new Date(year, month + 1, 0).getDate()
  }

  const renderCalendar = (month: number) => {
    const year = currentDate.getFullYear()
    const monthName = getMonthName(month)
    const daysInMonth = getDaysInMonth(month)

    const calendarDays = []
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i)
      const dayOfWeek = date.getDay()
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6

      calendarDays.push(
        <Center
          key={i}
          h="40px"
          borderTopColor="#DCE2EA"
          borderTopWidth="1px"
          cursor="pointer"
          position="relative"
          onClick={() => (getEventsOfTheDay(i, month, year, events).length > 0 ? setDateToShow(date) : {})}
        >
          <Text fontSize="lg" color={isWeekend ? '#7E8B9A' : '#171C22'}>
            {i}
          </Text>
          {getEventsOfTheDay(i, month, year, events).length > 0 && (
            <Box h="10px" w="10px" bgColor="#06C270" borderRadius="50%" position="absolute" bottom="-8px" />
          )}
        </Center>
      )
    }

    return (
      <Box>
        <Grid templateColumns="repeat(7, 1fr)" gap="28px">
          {Array.from({ length: new Date(currentDate.getFullYear(), month, 1).getDay() }).map((_, index) => (
            <Box key={`blank-${index}`} h="40px" />
          ))}
          <Center h="40px">
            <Text color="#171C22" fontSize="2xl" mt="50px">
              {monthName}
            </Text>
          </Center>
          {Array.from({ length: 6 - new Date(currentDate.getFullYear(), month, 1).getDay() }).map((_, index) => (
            <Box key={`blank-${index}`} h="40px" />
          ))}

          {Array.from({ length: new Date(currentDate.getFullYear(), month, 1).getDay() }).map((_, index) => (
            <Box key={`blank-${index}`} h="40px" />
          ))}
          {calendarDays}
        </Grid>
      </Box>
    )
  }

  const renderNextSixMonths = () => {
    const monthsToRender = 6
    const calendarComponents = []

    for (let i = 0; i < monthsToRender; i++) {
      const monthToRender = (currentMonth + i) % 12
      calendarComponents.push(renderCalendar(monthToRender))
    }

    return calendarComponents
  }

  return (
    <>
      {dateToShow != null ? (
        <>
          <ExtraOptionsModal
            isOpen={isExtraOptionsOpen}
            onClose={onExtraOptionsClose}
            buttons={[
              {
                text: 'Share with friends',
                onClick: () => {
                  onShareOpen()
                  onExtraOptionsClose()
                },
                isRed: false,
              },
              //{ text:'Share on Instagram', onClick:() => {}, isRed:false },
              { text: 'Cancel', onClick: onExtraOptionsClose, isRed: true },
            ]}
          />
          {!isMobile && (
            <Box>
              <IconButton
                position={isSmallTablet ? 'absolute' : 'static'}
                top="24px"
                left={isBigMobile ? '221px' : '256px'}
                minW="32px"
                h="32px"
                variant="light"
                aria-label="back"
                onClick={() => setDateToShow(null)}
                icon={
                  <Icon width="20px" height="19px" viewBox="0 0 23 20">
                    <ArrowLeft color="#171C22" />
                  </Icon>
                }
              />
            </Box>
          )}
          <SimpleGrid
            spacingX="32px"
            spacingY={'24px'}
            m="24px"
            templateColumns="repeat(auto-fit, minmax(350px, 1fr))"
            templateRows="auto"
          >
            {events?.events
              .filter((e: any) => isSameDay(new Date(e.start_time), dateToShow))
              .map((event: any, index: number) => {
                return (
                  <>
                    {event.type === 'private_class' ? (
                      <>
                        <PrivateClassJoinedCard
                          key={index}
                          id={event.id}
                          time={event.start_time}
                          duration={event.duration}
                          coach={event.coach}
                          package_id={event.package_id}
                          type={event.type}
                          isTablet={isTablet}
                          onExtraOptionsOpen={() => {
                            setCurrentEventId(event.id)
                            onExtraOptionsOpen()
                          }}
                        />
                      </>
                    ) : (
                      <EventDetailCard
                        key={index}
                        id={event.id}
                        title={event.title}
                        status={'hosted'}
                        time={new Date(event.start_time)}
                        type={event.type}
                        court={event.court}
                        level={getSkillLevelDisplay(event.skill_level)}
                        host={event.host}
                        coaches={event.coaches}
                        players={event.participants}
                        description={event?.description ?? ''}
                        price={event.fee}
                        duration={event.duration + ' minutes'}
                        onOpenWaitlist={() => {}}
                        thumbnail={event.venue?.images?.length > 0 ? event.venue.images[0] : ''}
                        location={event?.venue?.address}
                        locationName={event?.venue?.name}
                        //waitlistPosition={row.status?.position}
                        onExtraOptionsOpen={() => {
                          console.log('About to open extra options', event.id)
                          setCurrentEventId(event.id)
                          onExtraOptionsOpen()
                        }}
                      />
                    )}
                  </>
                )
              })}
          </SimpleGrid>
        </>
      ) : (
        <Box p={isMobile ? '20px' : '0px'}>
          <Grid templateColumns="repeat(7, 1fr)" borderBottom="1px" borderBottomColor="#DCE2EA" gap="28px">
            {DAYS_OF_WEEK.map((d: string, i: number) => {
              return (
                <Center h="17px" key={i}>
                  <Text color="#171C22" fontSize="sm">
                    {d}
                  </Text>
                </Center>
              )
            })}
          </Grid>

          {renderNextSixMonths()}
        </Box>
      )}
    </>
  )
}

export default CalendarDisplay
